import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { CacheOptions, cache } from '@/lib/cache';
import { logger } from '@/lib/logger';
import { OAuthTokenManager } from '@/lib/oauth-token-manager';

export interface ApiRequestConfig extends Omit<AxiosRequestConfig, 'headers'> {
  /**
   * Skip OAuth token injection (for public endpoints)
   */
  skipAuth?: boolean;
  /**
   * Additional headers to include
   */
  headers?: Record<string, string>;

  url?: string; // Allow url in config for flexibility
}

export interface QueryParams {
  page?: number;
  limit?: number;
  [key: string]: unknown;
}

export interface IPagination {
  page: number;
  limit: number;
  total?: number;
  [key: string]: unknown;
}

/**
 * A generic API response shape. This is intentionally loose to allow
 * the backend to evolve without breaking the client.
 */
export type ApiResponse<T = any> = T & {
  meta?: {
    query?: unknown;
    pagination?: IPagination;
    [key: string]: unknown;
  };
  data?: T;
  items?: T extends unknown[] ? T : never;
  [key: string]: unknown;
};
/**
 * Configuration for BaseHttpClient.
 */
export interface HttpClientConfig extends AxiosRequestConfig {
  /** Enable/disable request/response logging */
  enableLogging?: boolean;
  /** Enable/disable built-in axios interceptors */
  enableInterceptors?: boolean;
}

/**
 * Request options supported by BaseHttpClient.
 */
export interface RequestOptions extends AxiosRequestConfig {
  /** Optional bearer token to include in Authorization header */
  token?: string;
  /** Number of retry attempts on failure */
  retries?: number;
  /** Delay (ms) between retries */
  retryDelay?: number;
  /** Caching options for GET requests */
  cache?: CacheOptions;
  // Option to skip OAuth token injection
  skipAuth?: boolean;
}

export class BaseHttpClient {
  protected axiosInstance: AxiosInstance;
  protected config: HttpClientConfig;

  constructor(config: HttpClientConfig) {
    this.config = {
      timeout: 30000,
      enableLogging: true,
      enableInterceptors: true,
      ...config,
    };

    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
      // Custom params serializer for clean multi-value filters
      paramsSerializer: {
        serialize: (params) => {
          const searchParams = new URLSearchParams();
          Object.entries(params || {}).forEach(([key, value]) => {
            if (value === undefined || value === null) {
              return;
            }
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== undefined && item !== null && item !== '') {
                  searchParams.append(key, String(item));
                }
              });
            } else {
              searchParams.append(key, String(value));
            }
          });
          return searchParams.toString();
        },
      },
    });

    if (this.config.enableInterceptors) {
      this.setupInterceptors();
    }
  }

  /**
   * Setup request and response interceptors.
   */
  protected setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const requestOptions = config as unknown as RequestOptions;

        // If a token is explicitly provided, use it first (e.g. user JWT/session).
        if (requestOptions.token) {
          config.headers.set('Authorization', `Bearer ${requestOptions.token}`);
        }

        // Otherwise, if we're on the server and auth is not skipped, inject OAuth client credential token.
        if (!requestOptions.skipAuth && !requestOptions.token && typeof window === 'undefined') {
          try {
            const authorizationHeader = await OAuthTokenManager.getInstance().getAuthorizationHeader();

            // Use existing Authorization header if the caller set one explicitly
            const existingAuth = (config.headers as any)?.Authorization;
            if (!existingAuth) {
              config.headers.set('Authorization', authorizationHeader);
            }

            if (this.config.enableLogging) {
              logger.debug({
                msg: 'OAuth token injection',
                hasAuthorizationHeader: !!(config.headers as any)?.Authorization,
                usedCachedToken: true,
              });
            }
          } catch (err: any) {
            logger.error({ msg: 'OAuth token acquisition failed — aborting request', error: err?.message || err });
            return Promise.reject(err);
          }
        }

        if (this.config.enableLogging) {
          logger.debug({
            msg: 'API Request',
            method: config.method?.toUpperCase(),
            url: config.url,
            params: config.params,
          });
        }
        return config;
      },
      (error) => {
        if (this.config.enableLogging) {
          logger.error({
            msg: 'API Request Error',
            error: error.message,
          });
        }
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (this.config.enableLogging) {
          logger.debug({
            msg: 'API Response',
            status: response.status,
            url: response.config.url,
            data: (JSON.stringify(response.data ?? '') ?? '').slice(0, 100), // Truncate long responses
          });
        }
        return response;
      },
      (error) => {
        if (this.config.enableLogging) {
          logger.error({
            msg: 'API Response Error',
            status: error.response?.status,
            statusText: error.response?.statusText,
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            fullUrl: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
            errorCode: error.code,
            errorMessage: error.message,
            responseData: (JSON.stringify(error.response?.data ?? '') ?? '').slice(0, 100), // Truncate long responses
            ...(error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.code === 'ETIMEDOUT'
              ? {
                  connectionDetails: {
                    baseURL: error.config?.baseURL || this.config.baseURL,
                    timeout: error.config?.timeout || this.config.timeout,
                    code: error.code,
                  },
                }
              : {}),
          });
        }
        return Promise.reject(error);
      },
    );
  }

  /**
   * Execute a request with retry support.
   */
  protected async executeWithRetry<T>(requestFn: () => Promise<AxiosResponse<T>>, retries = 0, retryDelay = 1000): Promise<AxiosResponse<T>> {
    let lastError: any;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await requestFn();
      } catch (error: any) {
        lastError = error;
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        const isNetworkError = error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.code === 'ETIMEDOUT' || error.code === 'ENETUNREACH';

        if (this.config.enableLogging) {
          // Capture nested errors cleanly
          const getDetailedErrorInfo = (err: any): any => {
            const info: any = {
              code: err.code,
              message: err.message,
              syscall: err.syscall,
              address: err.address,
              port: err.port,
            };
            if (err.cause) {
              info.cause = getDetailedErrorInfo(err.cause);
            }
            if (err.errors && Array.isArray(err.errors)) {
              info.aggregateErrors = err.errors.map((e: any) => ({
                code: e.code,
                message: e.message,
                syscall: e.syscall,
                address: e.address,
                port: e.port,
              }));
            }
            return info;
          };

          const detailedError = getDetailedErrorInfo(error);
          const errorDetails: any = {
            message: `Request attempt ${attempt + 1}/${retries + 1} failed`,
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            fullUrl: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
            errorCode: error.code || detailedError.cause?.code,
            errorMessage: error.message,
            isTimeout,
            isNetworkError,
            detailedError,
          };

          if (isNetworkError) {
            const syscallSource = (detailedError.aggregateErrors && detailedError.aggregateErrors[0]) || detailedError.cause || detailedError;
            errorDetails.connectionDetails = {
              baseURL: error.config?.baseURL || this.config.baseURL,
              timeout: error.config?.timeout || this.config.timeout,
              attemptedHost: error.config?.baseURL || this.config.baseURL,
              syscallInfo: {
                syscall: syscallSource.syscall,
                address: syscallSource.address,
                port: syscallSource.port,
                errno: syscallSource.errno,
              },
              allAttempts:
                (detailedError.aggregateErrors || detailedError.cause?.aggregateErrors || []).map((e: any) => ({
                  address: e.address,
                  port: e.port,
                  message: e.message,
                })) || undefined,
            };
          }

          if (attempt < retries) {
            const nextDelay = Math.pow(2, attempt) * retryDelay;
            errorDetails.retryInfo = {
              nextRetryIn: `${nextDelay}ms`,
              remainingRetries: retries - attempt,
            };
          }

          logger.warn(errorDetails);
        }

        if (attempt < retries) {
          const delay = Math.pow(2, attempt) * retryDelay;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    if (this.config.enableLogging && lastError) {
      let rootCause = lastError.errors?.[0] || lastError.cause;
      if (rootCause && !rootCause.message && rootCause.errors?.[0]) {
        rootCause = rootCause.errors[0];
      }

      logger.error({
        msg: 'All retry attempts exhausted',
        totalAttempts: retries + 1,
        method: lastError.config?.method?.toUpperCase(),
        fullUrl: lastError.config?.baseURL ? `${lastError.config.baseURL}${lastError.config.url}` : lastError.config?.url,
        errorCode: lastError.code,
        errorMessage: lastError.message,
        rootCause: rootCause
          ? {
              code: rootCause.code,
              message: rootCause.message,
              syscall: rootCause.syscall,
              address: rootCause.address,
              port: rootCause.port,
            }
          : undefined,
        allConnectionAttempts:
          lastError.errors?.length > 1 || rootCause?.errors?.length > 1
            ? (lastError.errors || rootCause?.errors || []).map((e: any) => ({
                address: e.address,
                port: e.port,
                message: e.message,
              }))
            : undefined,
        stack: lastError.stack?.split('\n').slice(0, 5).join('\n'),
      });
    }

    throw lastError;
  }

  /**
   * Generic GET request
   */
  public async get<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 1000, token, cache: cacheOptions, ...axiosConfig } = options || {};

    const makeRequest = () => {
      const requestFn = () =>
        this.axiosInstance.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
      return this.executeWithRetry(requestFn, retries, retryDelay);
    };

    if (cacheOptions) {
      const cacheKey = cacheOptions.key ?? cache.generateKey(url, axiosConfig.params);
      if (!cacheOptions.forceRefresh) {
        const cached = cache.get<ApiResponse<T>>(cacheKey);
        if (cached) return cached;
      }

      const response = await makeRequest();
      const rawCached = response.data;
      const result = rawCached && Array.isArray(rawCached.items) ? { ...rawCached, data: rawCached.items } : rawCached;
      cache.set(cacheKey, result, cacheOptions.ttl, cacheOptions.tags);
      return result;
    }

    const response = await makeRequest();
    const raw = response.data;
    if (raw && Array.isArray(raw.items)) {
      return { ...raw, data: raw.items };
    }

    return raw;
  }

  /**
   * Generic POST request
   */
  public async post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 1000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.post(url, data, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawPost = response.data;
    if (rawPost && Array.isArray(rawPost.items)) {
      return { ...rawPost, data: rawPost.items };
    }
    return rawPost;
  }

  /**
   * Generic PUT request
   */
  public async put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 1000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.put(url, data, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawPut = response.data;
    if (rawPut && Array.isArray(rawPut.items)) {
      return { ...rawPut, data: rawPut.items };
    }
    return rawPut;
  }

  /**
   * Generic PATCH request
   */
  public async patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 1000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.patch(url, data, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawPatch = response.data;
    if (rawPatch && Array.isArray(rawPatch.items)) {
      return { ...rawPatch, data: rawPatch.items };
    }
    return rawPatch;
  }

  /**
   * Generic HEAD request
   * Note: HEAD responses typically don't have a body, but this method is included for completeness.
   * It will return the headers and status, and attempt to normalize any body if present.
   */
  public async head<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 2000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.head(url, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawHead = response.data;
    if (rawHead && Array.isArray(rawHead.items)) {
      return { ...rawHead, data: rawHead.items };
    }
    return rawHead;
  }

  /**
   *  Generic OPTIONS request
   *  Note: OPTIONS responses typically don't have a body, but this method is included for completeness.
   *  It will return the headers and status, and attempt to normalize any body if present.
   */
  public async options<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 2000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.options(url, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawOptions = response.data;
    if (rawOptions && Array.isArray(rawOptions.items)) {
      return { ...rawOptions, data: rawOptions.items };
    }
    return rawOptions;
  }

  /**
   * Generic DELETE request
   */
  public async delete<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 2000, token, ...axiosConfig } = options || {};
    const requestFn = () =>
      this.axiosInstance.delete(url, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawDel = response.data;
    if (rawDel && Array.isArray(rawDel.items)) {
      return { ...rawDel, data: rawDel.items };
    }
    return rawDel;
  }

  /**
   * Generic request method
   */
  public async request<T = any>(config: RequestOptions): Promise<ApiResponse<T>> {
    const { retries = 0, retryDelay = 2000, token, ...axiosConfig } = config;
    const requestFn = () =>
      this.axiosInstance.request({
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    const response = await this.executeWithRetry(requestFn, retries, retryDelay);
    const rawReq = response.data;
    if (rawReq && Array.isArray(rawReq.items)) {
      return { ...rawReq, data: rawReq.items };
    }
    return rawReq;
  }

  /**
   * Get the underlying axios instance
   */
  public getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}
